import Group from "../group";
import { ascending } from "@/util/sort";
import Field from "../field";

class GroupBuilder {
    _calculatedGroupDisplayOrder = 1000000000;
    _calculatedGroupName = "Calculated Fields";

    constructor(graph, type, pathString, filter = "", overrideType = null, enabledCalculatedFieldCategories = false) {
        [this._graph, this._type, this._overrideType, this._filter] = [graph, type, overrideType, filter];

        this._typePath = pathString;
        this._enabledCalculatedFieldCategories = enabledCalculatedFieldCategories;

        this._entityTypes = [];
        this._containerCount = 0;

        if (graph.pathEntityTypes && graph.pathEntityTypes.length > 0) {
            this._entityTypes.push(...graph.pathEntityTypes);
        }

        let t = type.value;
        while (!!t) {
            if (t.entityType) {
                this._entityTypes.push(t.entityType);
            }

            if (t.container) {
                this._containerCount++;
            }

            t = t.parent || t.container;
        }

        this._groups = [];
        this._groupedFields = [];

        this._ungrouped = new Group(null, -10000, null);
        this._calculatedGroups = [];
    }

    build() {
        const type = this._overrideType ? this._overrideType.value : this._type.value;
        const graph = this._graph;
        let groups = this._groups;

        type.fieldGroups.forEach(g => {
            const group = new Group(g.name, g.displayOrder, g.subtitle);

            g.types.forEach(t => {
                const inheritedType = graph.getType(t.name).value;

                inheritedType.getFields().forEach(f => {
                    this._add(f, group);
                });
            });

            g.fields.forEach(f => {
                const match = type.fields.find(field => field.name === f);

                if (this._add(match, group)) {
                    this._groupedFields.push(f);
                }
            });

            group.fields.sort(ascending(field => field.displayOrder));
            groups.push(group);
        });

        if (type.inheritedTypes) {
            type.inheritedTypes.forEach(typeName => {
                const inheritedType = graph.getType(typeName);
                const builder = new GroupBuilder(graph, this._type, this._typePath, this._filter, inheritedType, this._enabledCalculatedFieldCategories);
                const inheritedGroups = builder.build();
                this._merge(inheritedGroups);
            })
        }

        type.fields.forEach(field => {
            if (this._groupedFields.indexOf(field.name) < 0) {
                this._add(field, this._ungrouped);
            }
        });

        this._ungrouped.fields.sort(ascending(field => field.value.displayOrder, { nullsLast: false }));

        this._calculatedGroups
            .forEach(g => g.fields.sort(ascending(field => field.value.displayName, { nullsLast: false })));

        groups.push(...this._calculatedGroups.filter(g => g.fields.length));

        groups.forEach(group => {
            group.fields.sort(ascending(field => field.value.displayOrder, { nullsLast: false }));

            if (!group.displayOrder && group.fields.length > 0) {
                group.displayOrder = group.fields[0].displayOrder;
            }

            if (!group.displayOrder) {
                group.displayOrder = this._calculatedGroupDisplayOrder - 1;
            }
        });

        if (this._ungrouped.fields.length > 0) {
            groups.push(this._ungrouped);
        }

        groups = groups.filter(x => x.fields.length > 0);
        groups.sort(ascending(group => group.displayOrder));

        return groups;
    }

    _merge(groups) {
        groups.forEach(group => {
            if (!group.name) {
                this._mergeGroupFields(group, this._ungrouped);
            } else if (group.name === "Calculated Fields") {
                const calculatedGroup = this._getCalculatedGroup(group.subtitle);
                this._mergeGroupFields(group, calculatedGroup);
            } else {
                const local = this._groups.find(x => x.name === group.name);
                if (!local) {
                    this._groups.push(group);
                } else {
                    this._mergeGroupFields(group, local);
                }
            }
        })
    }

    _mergeGroupFields(source, destination) {
        source.fields.forEach(field => {
            const match = destination.fields.find(x => x.value.name === field.value.name);
            if (!match) {
                destination.fields.push(field);
            }
        });
    }

    _add(field, group) {
        if (!this._canAdd(field, group)) {
            return false;
        }

        var fieldObj = new Field(field, this._graph);

        if (!!field.calculatedField) {
            const calculatedGroup = this._getCalculatedGroup(field.calculatedField.category);

            calculatedGroup.fields.push(fieldObj);
        } else {
            group.fields.push(fieldObj);
        }

        return true;
    }

    _getCalculatedGroup(category) {
        const subtitle = this._enabledCalculatedFieldCategories ? category : null;

        let calculatedGroup = this._calculatedGroups.find(g => g.subtitle === subtitle);

        if (!calculatedGroup) {
            calculatedGroup = new Group(this._calculatedGroupName, this._calculatedGroupDisplayOrder + this._calculatedGroups.length, subtitle);
            this._calculatedGroups.push(calculatedGroup);
        }

        return calculatedGroup;
    }

    _matchesFilter(field, group) {
        if(!this._filter) {
            return true;
        }

        const filter = this._filter.toLowerCase();
        
        if((field.name && field.name.toLowerCase().indexOf(filter) !== -1)
                || (field.displayName && field.displayName.toLowerCase().indexOf(filter) !== -1)) {
            return true;
        }

        if(group.name && group.name.toLowerCase().indexOf(filter) !== -1) {
            return true;
        }

        if(field.calculatedField) {
            if(this._calculatedGroupName.toLowerCase().indexOf(filter) !== -1 ||
                    (field.calculatedField.category && field.calculatedField.category.toLowerCase().indexOf(filter) !== -1)) {
                return true;
            }
        }

        return false;
    }

    _canAdd(field, group) {
        if (!this._matchesFilter(field, group)) {
            return false;
        }

        const calc = field.calculatedField;

        if (!calc) {
            return true;
        }

        if (!calc.typePath) {
            return this._type.value.kind === "instance" || !calc.entityType || this._entityTypes.indexOf(calc.entityType) != -1;
        }

        const calcTypePathParts = calc.typePath.split(".");
        const typePathParts = this._typePath.split(".");
        if (calcTypePathParts.length - this._containerCount > typePathParts.length) {
            return false;
        }

        for (let i = calcTypePathParts.length - this._containerCount; i >= 0; i--) {
            if (calcTypePathParts[i] !== typePathParts[i])
                return false;
        }

        return true;
    }
}

export default GroupBuilder;